<template>
  <div class="p-4 max-w-3xl mx-auto">
    <header class="pb-5 border-b border-gray-200">
      <h2 class="font-bold text-lg">WhatsApp</h2>
      <p class="text-sm text-gray-600 mt-1">
        Cuando esta activado, las credenciales de acceso se envian por correo y tambien por
        WhatsApp.
      </p>
    </header>

    <div v-if="loading" class="mt-8 text-center text-gray-500">Cargando configuracion...</div>

    <template v-else>
      <!-- Estado actual -->
      <div
        class="mt-5 p-3 rounded-md text-sm"
        :class="settings.is_ready ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-800'"
      >
        {{
          settings.is_ready
            ? 'Listo: los mensajes de WhatsApp se estan enviando.'
            : 'Todavia no se envian mensajes. Falta activarlo o completar los datos de Meta.'
        }}
      </div>

      <!-- Configuracion -->
      <section class="mt-6 space-y-6">
        <div class="flex items-center">
          <BaseSwitch :value="form.enabled" @on="form.enabled = true" @off="form.enabled = false" />
          <span class="pl-3">Enviar credenciales tambien por WhatsApp</span>
        </div>

        <BaseInput
          label="ID del numero de telefono"
          placeholder="ID del numero de telefono"
          :error="errors.first('phone_number_id')"
          v-model="form.phone_number_id"
        />
        <p class="text-xs text-gray-500 -mt-4">
          Lo da Meta en su panel, seccion WhatsApp &gt; Configuracion de la API. Son puros numeros.
        </p>

        <BaseInput
          label="Token de acceso"
          :placeholder="tokenPlaceholder"
          type="password"
          :error="errors.first('access_token')"
          v-model="accessToken"
        />
        <p class="text-xs text-gray-500 -mt-4">
          {{
            settings.has_access_token
              ? 'Ya hay un token guardado. Dejalo vacio para conservarlo.'
              : 'Token permanente de Meta. Se guarda encriptado.'
          }}
        </p>

        <BaseInput
          label="Nombre de la plantilla"
          placeholder="Nombre de la plantilla"
          :error="errors.first('template_name')"
          v-model="form.template_name"
        />

        <BaseInput
          label="Idioma de la plantilla"
          placeholder="es_MX"
          :error="errors.first('template_language')"
          v-model="form.template_language"
        />

        <BaseInput
          label="Lada del pais"
          placeholder="52"
          :error="errors.first('default_country_code')"
          v-model="form.default_country_code"
        />
        <p class="text-xs text-gray-500 -mt-4">
          Solo se usa para telefonos guardados sin lada (los capturados antes de poder elegir pais).
          Mexico es 52.
        </p>

        <BaseButton class="w-full py-3" @click="handleSave" :disabled="saving">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </BaseButton>
      </section>

      <!-- Prueba -->
      <section class="mt-10 pt-6 border-t border-gray-200 space-y-4">
        <h3 class="font-bold">Enviar mensaje de prueba</h3>
        <p class="text-sm text-gray-600">
          Manda la plantilla con datos de ejemplo para revisar que Meta este bien configurado.
        </p>

        <PhoneInput label="Telefono" placeholder="Telefono" v-model="testPhone" />

        <BaseButton class="w-full py-3" secondary @click="handleTest" :disabled="testing">
          {{ testing ? 'Enviando...' : 'Enviar prueba' }}
        </BaseButton>
      </section>
    </template>
  </div>
</template>

<script>
import Errors from '@/util/Errors';
import BaseButton from '@/components/BaseButton';
import BaseInput from '@/components/BaseInput';
import BaseSwitch from '@/components/BaseSwitch.vue';
import PhoneInput from '@/components/PhoneInput';

export default {
  name: 'WhatsappSettings',
  components: { BaseButton, BaseInput, BaseSwitch, PhoneInput },
  data() {
    return {
      settings: null,
      form: {
        enabled: false,
        phone_number_id: '',
        template_name: '',
        template_language: '',
        default_country_code: '',
      },
      // Se maneja aparte del resto: vacio significa "no cambiar el token".
      accessToken: '',
      testPhone: '',
      loading: true,
      saving: false,
      testing: false,
      errors: new Errors(),
    };
  },
  computed: {
    tokenPlaceholder() {
      return this.settings && this.settings.has_access_token ? 'Token guardado' : 'Token de acceso';
    },
  },
  async created() {
    try {
      this.setSettings(await this.$store.dispatch('whatsapp/fetchSettings'));
    } catch (error) {
      this.$store.dispatch('notification/addError', 'No se pudo cargar la configuracion');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    setSettings(settings) {
      this.settings = settings;
      this.form = {
        enabled: settings.enabled,
        phone_number_id: settings.phone_number_id || '',
        template_name: settings.template_name || '',
        template_language: settings.template_language || '',
        default_country_code: settings.default_country_code || '',
      };
      this.accessToken = '';
    },

    async handleSave() {
      this.saving = true;
      this.errors.clear();

      try {
        this.setSettings(
          await this.$store.dispatch('whatsapp/updateSettings', {
            ...this.form,
            access_token: this.accessToken,
          }),
        );
        this.$store.dispatch('notification/addSuccess', 'Configuracion guardada');
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors.capture(error.response.data.errors);
        }
        this.$store.dispatch('notification/addError', 'No se pudo guardar la configuracion');
      } finally {
        this.saving = false;
      }
    },

    async handleTest() {
      this.testing = true;

      try {
        const result = await this.$store.dispatch('whatsapp/sendTest', this.testPhone);
        this.$store.dispatch('notification/addSuccess', result.message);
      } catch (error) {
        const message =
          error.response && error.response.data.message
            ? error.response.data.message
            : 'No se pudo enviar el mensaje de prueba';
        this.$store.dispatch('notification/addError', message);
      } finally {
        this.testing = false;
      }
    },
  },
};
</script>
